/** @jsxImportSource @emotion/react */
import Step, { type IStepProps } from 'modules/main/ui/components/stepper/components/step/step';
import React, { useState } from 'react';
import { useCollapsibleAnimationStyles } from 'shared/utils';
import type { DivProps } from 'typings';
import { ClickableArea } from 'ud-ui/components';
import EmptyButton from 'ud-ui/components/empty-button';
import ArrowDashIcon from 'ud-ui/components/icon/arrow-dash-icon';
import CloseIcon from 'ud-ui/components/icon/close-icon';
import Text from 'ud-ui/components/text';
import * as S from './styles';

interface IProps extends DivProps {
  header: string;
  subtitle?: string;
  onClose?: () => void;
  isOpened: boolean;
  steps: IStepProps[];
}

function Stepper(props: IProps) {
  const { ...rest } = props;

  const { ref, styles } = useCollapsibleAnimationStyles({
    isOpen: props.isOpened,
    maxHeight: '309px',
    additionalStyles: { closed: { padding: 0 }, open: { paddingTop: '28px' } },
    exitStyles: { display: 'none' },
  });

  // Закрытый с самого начала блок не рисуем: он оставался бы пустым элементом высотой 0, а `gap` родителя
  // считает его дважды — между новостями и карточками вставало 40 px вместо одного отступа.
  // Состояние, а не `ref.current`, который React не советует писать при рендере: открытый блок запоминается
  // в том же проходе (`setState` во время рендера допустим, когда значение выводится из пропсов).
  const [wasOpened, setWasOpened] = useState(props.isOpened);
  if (props.isOpened && !wasOpened) setWasOpened(true);
  if (!wasOpened) return null;

  return (
    <S.MainBlock {...rest} ref={ref} style={styles}>
      <S.CloseBlock>
        <EmptyButton
          onClick={props.onClose}
          icon={
            <ClickableArea horizontalExtend={10} verticalExtend={10}>
              <CloseIcon />
            </ClickableArea>
          }
        />
      </S.CloseBlock>
      <S.Header className="mb-13">
        <Text as="h2" variant="title-3">
          {props.header}
        </Text>
        {props.subtitle && <Text variant="body-2">{props.subtitle}</Text>}
      </S.Header>
      <S.Steps>
        {props.steps.map((step, index) => (
          <>
            <Step {...step} key={step.emoji?.toString() ?? index} />
            {index !== props.steps.length - 1 && <ArrowDashIcon id={`icon-${index}`} />}
          </>
        ))}
      </S.Steps>
    </S.MainBlock>
  );
}

export default Stepper;
